import {Card} from '@material-ui/core';
import {changeMaxPage} from 'Agent/Accounts/ducks/account';
import {Page} from 'Core/models/Page';
import {getFeatureToggleVariable} from 'feature';
import React, {CSSProperties, ReactNode, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import {VariableSizeList as List} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {ReducerState} from 'rootReducer';
import CardField from 'Shared/components/CardField';
import 'Shared/components/PagedView/Card/CardBody.sass';
import {CardFilters} from 'Shared/components/PagedView/Card/CardFilterOverlay';
import {Column} from 'Shared/components/PagedView/PagedView';
import PagedViewLoadNext, {PagedViewLoadNextProps} from 'Shared/ducks/PagedView';
import PagedViewSnackBar from '../PageLimitSnackbar';

type CardBodyProps<TData> = {
  data: Page<TData>;
  filters: Array<CardFilters<any>>;
  loadMore: (page: Page<TData>) => void;
  columns: Array<Column<TData>>;
  pagedViewKey: string;
};

interface CardInfoProps<TData extends {}> {
  index: number;
  columns: Column<TData>[];
  style: CSSProperties;
  data: Page<TData>;
}

export const CardInfo = <TData extends {}>(cardInfo: CardInfoProps<TData>) => (
  <div style={cardInfo.style}>
    <Card className="paged-card">
      {cardInfo.columns.map(col => (
        <div key={`${col.title}_${cardInfo.index}`}>
          <TableCell
            title={col.title}
            renderColumn={col.getContent}
            index={cardInfo.index}
            data={cardInfo.data}
            ddprivacy={col.ddprivacy ? col.ddprivacy : 'allow'}
          />
        </div>
      ))}
    </Card>
  </div>
);

const CardBody = <TData extends {}>(props: CardBodyProps<TData>) => {
  const {columns, data} = props;
  const dispatch = useDispatch();
  const [maxOpen, setMaxOpen] = useState(true);
  const [warningOpen, setWarningOpen] = useState(true);
  const onCloseMax = () => {
    setMaxOpen(false);
  };
  const onCloseWarning = () => {
    setWarningOpen(false);
  };
  const {maxPage} = useSelector((state: ReducerState) => state.account);
  const {PAGE_LIMIT, RECORDS_PER_PAGE} = getFeatureToggleVariable();

  return (
    <>
      <div className="paged-card__content">
        <AutoSizer>
          {({height, width}) => (
            <InfiniteLoader
              isItemLoaded={index => !!props.data.result[index]}
              itemCount={props.data.totalCount}
              loadMoreItems={(start, stop) => {
                const infoPagedViewLoadNext: PagedViewLoadNextProps<TData> = {
                  data: props.data,
                  stop: stop,
                  start: start,
                  pagedViewKey: props.pagedViewKey,
                  loadMore: props.loadMore,
                  RECORDS_PER_PAGE: RECORDS_PER_PAGE,
                  PAGE_LIMIT: PAGE_LIMIT,
                  dispatch: dispatch,
                  changeMaxPage: changeMaxPage,
                };
                PagedViewLoadNext(infoPagedViewLoadNext);
              }}
            >
              {({onItemsRendered, ref}) => (
                <List
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  height={height}
                  width={width}
                  itemCount={props.data.result.length}
                  itemSize={() => columns.length * 31 + 58}
                >
                  {({index, style}) =>
                    props.pagedViewKey != 'accounts' ? (
                      <div style={style}>
                        <Card className="paged-card">
                          {columns.map(col => (
                            <div key={`${col.title}_${index}`}>
                              <TableCell
                                title={col.title}
                                renderColumn={col.getContent}
                                index={index}
                                data={data}
                                ddprivacy={col.ddprivacy ? col.ddprivacy : 'allow'}
                              />
                            </div>
                          ))}
                        </Card>
                      </div>
                    ) : (
                      <PagedViewSnackBar
                        index={index + 1}
                        style={style}
                        maxPage={maxPage}
                        maxOpen={maxOpen}
                        RECORDS_PER_PAGE={RECORDS_PER_PAGE}
                        PAGE_LIMIT={PAGE_LIMIT}
                        onCloseMax={onCloseMax}
                        onCloseWarning={onCloseWarning}
                        warningOpen={warningOpen}
                        infoRow={CardInfo({index, columns, style, data})}
                        isMobile={true}
                      />
                    )
                  }
                </List>
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      </div>
    </>
  );
};

const TableCell = React.memo(
  <TData extends {}>(props: {
    renderColumn: (data: any) => ReactNode;
    index: number;
    data: Page<TData>;
    title: string;
    ddprivacy?: string;
  }) => {
    const {renderColumn, data, index, title, ddprivacy} = props;
    const {result} = data;
    const content = useMemo(() => renderColumn(result[index]), [index, renderColumn, result]);

    return title ? (
      <CardField className="card-body__field" name={title} value={content} ddprivacy={ddprivacy} />
    ) : (
      <>{content}</>
    );
  }
);

export default CardBody;
